import { EncodedDrawAction, DrawActionCodec } from '@engoo/realtime-protocol/draw';
import { drawReducer, DrawState } from 'app/models/rtc/draw/reducer';
import { ILazyActionHandler } from 'app/services/rtc/sync/documentmanagers/lazy';
import { ActionState } from 'app/services/rtc/sync/documentmanagers';
import { EntityStringId } from 'app/types/entity';
import {
  IEncodedPersistedAction,
  makeStreamPosition,
  PersistedActionCodec,
} from '@engoo/realtime-protocol/persisted';
import { makeTimestamp } from '@engoo/realtime-protocol/util/timeSchemas';
import compress from 'app/models/rtc/draw/compress';
import simplify, { ISimplifyDeps } from 'app/models/rtc/draw/simplify';
import { decodeOrThrow } from 'app/utils/ioTs';
import { mapIterable } from 'app/utils/iterable';

/**
 * Action handler implementation for drawing.
 *
 * Handles applying actions speculatively for local user and
 * simplifying paths before sending.
 */
export default class DrawActionHandler implements ILazyActionHandler<DrawState, EncodedDrawAction> {
  public constructor(
    private caller: EntityStringId,
    private simplifyDeps: ISimplifyDeps,
  ) {}

  /**
   * Applies pending updates with the `caller` provided in the
   * constructor. This is necessary for applying changes to local
   * user's state.
   */
  public apply(doc: DrawState, action: EncodedDrawAction, actionState: ActionState) {
    switch (actionState) {
      case ActionState.Pending:
        return drawReducer(doc, this.fakePeristedAction(doc, action), actionState);
      case ActionState.Confirmed:
      case ActionState.ConfirmedOutOfOrder:
        return drawReducer(doc, action, actionState);
    }
  }

  /**
   * Reduces number of actions by removing redundant state change
   * actions, merging consecutive traces and simplifying paths.
   */
  public compress(doc: DrawState, actions: Iterable<EncodedDrawAction>) {
    // Decoding and encoding back and forth should be gone with
    // https://github.com/iknow/issues/issues/1609
    const decoded = mapIterable(actions, a => decodeOrThrow(DrawActionCodec, a));
    const reduced = compress(decoded, doc, this.caller);

    const { simplifyDeps } = this;
    const simplified = simplifyDeps
      ? mapIterable(reduced, ({ action, compressed }) => ({
        action: action && simplify(simplifyDeps, action),
        compressed,
      }))
      : reduced;

    return mapIterable(simplified, ({ action, compressed }) => ({
      action: action && DrawActionCodec.encode(action),
      compressed,
    }));
  }

  private fakePeristedAction(doc: DrawState, action: EncodedDrawAction): EncodedDrawAction & IEncodedPersistedAction {
    // Pretend to be a persisted action
    return {
      ...action,
      ...PersistedActionCodec.encode({
        position: makeStreamPosition(doc.version + 1),
        timestamp: makeTimestamp(0),
        user: this.caller,
      }),
    };
  }
}
